import {WishlistStreamItem} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/wishlist/v1/page/stream.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {TrackWishlistPageView} from '@aboutyou/tracking';
import {
  mediaDesktopAndUp,
  mediaLargeScreens,
  mobileOnly,
  Slot,
} from '@aboutyou/ui-components';
import React, {Fragment, useMemo} from 'react';
import styled, {css} from 'styled-components';
import {ProductStreamOffsetLimitPagination} from '../../../features/ProductStreamPagination/ProductStreamOffsetLimitPagination';
import {WishlistItemGrid} from '../WishlistItemGrid/WishlistItemGrid';
import {WishlistProductTile} from '../WishlistProductTile/WishlistProductTile';
import {WishlistSoldoutProductTile} from '../WishlistSoldoutProductTile/WishlistSoldoutProductTile';
import {WishlistEmpty} from './WishlistEmpty';
import {WishlistLoadingComponent} from './WishlistLoadingComponent';
import {WishlistStreamViewProps} from './WishlistPageStreamView';
import {WishlistSubscribeToNewsletterBanner} from './WishlistSubscribeToNewsletterBanner/WishlistSubscribeToNewsletterBanner';

const WISHLIST_CURRENTPAGE_STATE_KEY = 'WISHLIST_CURRENTPAGE_STATE_KEY';

const useWishlistSlicedItems = (items: WishlistStreamItem[]) => {
  /**
   * We are using a history page state to preserve the value on e.g history back
   */
  const [currentPage, setCurrentPage] = useHistoryPageState(1, {
    uniqueKey: WISHLIST_CURRENTPAGE_STATE_KEY,
  });
  const currentItems = currentPage * 32;
  return {items: items.slice(0, currentItems - 1), setCurrentPage, currentPage};
};

export const WishlistStreamView: React.FC<WishlistStreamViewProps> = ({
  allItemsTab,
  showConfirmOnDeleteItem,
  onClickAddToBasket,
  onClickSoldoutCta,
  gridColumns,
  smallAddToBasketCtaText,
  ProductTileComponent,
  isDataFetched,
  isDataFetching,
  enableImageGalleryAnimation,
}) => {
  const {t} = useTranslation();
  const items = allItemsTab.items;

  const {
    items: slicedItems,
    setCurrentPage,
    currentPage,
  } = useWishlistSlicedItems(items);

  const getFirstTileIndex = () => {
    const firstProductIndex = slicedItems.findIndex(
      item => item.type?.$case === 'deprecatedProduct',
    );
    return firstProductIndex === -1 ? 0 : firstProductIndex;
  };

  const firstTileIndex = useMemo(() => {
    return getFirstTileIndex();
  }, [slicedItems]);

  // Separate from `firstTileIndex` above (which checks a `deprecatedProduct`
  // case that no longer appears in the switch below, so it always falls back
  // to `0`) — this is used for the "Skip to results" skip-link anchor and
  // needs to land on an actual renderable product tile, not just index 0.
  const firstProductTileIndex = useMemo(
    () => slicedItems.findIndex(item => item.type?.$case === 'product'),
    [slicedItems],
  );

  return (
    <Fragment>
      <TrackWishlistPageView
        itemStream={items}
        isDataFetching={isDataFetching}
        isDataFetched={isDataFetched}
      />
      <WishlistItemGrid>
        {slicedItems.map((item, index) => {
          switch (item.type?.$case) {
            case 'product':
              return (
                <WishlistProductTile
                  id={
                    index === firstProductTileIndex
                      ? 'first-product-tile'
                      : undefined
                  }
                  data-skip-link={
                    index === firstProductTileIndex
                      ? t('Skip to results')
                      : undefined
                  }
                  smallAddToBasketCtaText={smallAddToBasketCtaText}
                  key={item.type.product.itemKey}
                  streamPosition={item.streamPosition}
                  tile={item.type.product.product}
                  showConfirmOnDeleteItem={showConfirmOnDeleteItem}
                  onClickAddToBasket={onClickAddToBasket}
                  onSoldoutCtaClick={onClickSoldoutCta}
                  gridColumns={gridColumns}
                  ProductTileComponent={ProductTileComponent}
                  enableImageGalleryAnimation={
                    index === firstTileIndex && enableImageGalleryAnimation
                  }
                  trackingData={{
                    blockTypeAndId: 'product_stream',
                  }}
                />
              );
            case 'soldoutProduct':
              return (
                <WishlistSoldoutProductTile
                  key={item.type.soldoutProduct.itemKey}
                  tile={item.type.soldoutProduct.soldoutProduct}
                  itemKey={item.type.soldoutProduct.itemKey}
                  streamPosition={item.streamPosition}
                  onClickSoldoutCta={onClickSoldoutCta}
                  showConfirmOnDeleteItem={showConfirmOnDeleteItem}
                  gridColumns={gridColumns}
                />
              );
            case 'emptyWishlist':
              return (
                <Fragment>
                  {isDataFetching && (
                    <WishlistLoadingComponent onlyPlaceholder />
                  )}

                  {!isDataFetching && (
                    <FullSpanItem
                      withLargeVerticalPadding
                      key={item.streamPosition}
                    >
                      <WishlistEmpty />
                    </FullSpanItem>
                  )}
                </Fragment>
              );
            case 'newsletterBanner':
              return (
                <Slot position={index} Comp="li">
                  <WishlistSubscribeToNewsletterBanner />
                </Slot>
              );
            default:
              return null;
          }
        })}
        <ProductStreamOffsetLimitPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
          Wrapper={ListItem}
        />
      </WishlistItemGrid>
    </Fragment>
  );
};

const ListItem = styled.li``;

const loadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 10000,
  tilesBeforeConsecutivelyDisplay: 10000,
};

const FullSpanItem = styled.li<{
  padded?: boolean;
  withLargeVerticalPadding?: boolean;
}>`
  margin: ${({padded}) => padded && '20px'} 0px;

  ${({withLargeVerticalPadding}) =>
    withLargeVerticalPadding &&
    css`
      margin: 50px 0px;
    `};

  grid-column: span 2;

  @media ${mediaDesktopAndUp} {
    grid-column: span 3;
  }

  @media ${mediaLargeScreens} {
    grid-column: span 4;
    ${({withLargeVerticalPadding}) =>
      withLargeVerticalPadding &&
      css`
        margin: 100px 0px;
      `};
  }

  ${mobileOnly(css`
    text-align: center;
  `)}
`;
